import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ButtonBase from '@mui/material/ButtonBase'
import { sand, amber } from '@tm/ui/theme/tokens/semantic'

/**
 * The shared toast: one dark pill for outcomes and undo, replacing the
 * per-feature <Snackbar><Alert/></Snackbar> pairs (this is the fourth pattern —
 * new code uses this one). It inverts against the app like the batch bar: dark
 * in light mode, light in dark mode. The amber action ink only rides the dark
 * pill (light mode, ~10:1 contrast); on the inverted light pill (dark mode) the
 * action uses the same ink as the message text instead, since amber never sits
 * on a white surface.
 *
 * Copy rules for every message that lands here (from the modernization doc §4.5):
 *   1. What broke — name the thing ("Couldn't hide 2 reviews"), never "Something went wrong".
 *   2. What it means — "They stay public until it works."
 *   3. The way out — one concrete action (Retry / Undo). Never "please try again later", never "Oops".
 */
export type ToastProps = {
  open: boolean
  message: string
  onClose: () => void
  action?: { label: string; onClick: () => void }
  autoHideDuration?: number
}

export function Toast({ open, message, onClose, action, autoHideDuration = 6000 }: ToastProps) {
  return (
    <Snackbar
      open={open}
      onClose={(_, reason) => {
        // Undo toasts must survive a stray click elsewhere; only time and the
        // explicit action dismiss them.
        if (reason !== 'clickaway') onClose()
      }}
      autoHideDuration={autoHideDuration}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={theme => {
          const isLight = theme.palette.mode === 'light'
          return {
            backgroundColor: isLight ? sand[900] : sand[0],
            color: isLight ? sand[25] : sand[900],
            borderRadius: 1,
            px: 1.75,
            py: 1.25,
            boxShadow: theme.shadows[8],
          }
        }}>
        <Typography variant="body2" component="span">
          {message}
        </Typography>
        {action && (
          <ButtonBase
            onClick={action.onClick}
            sx={theme => ({
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.body2.fontSize,
              fontWeight: 600,
              color: theme.palette.mode === 'light' ? amber[400] : sand[900],
              borderRadius: 0.75,
              px: 0.5,
            })}>
            {action.label}
          </ButtonBase>
        )}
      </Stack>
    </Snackbar>
  )
}
